import React, { useState } from "react";
import {
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import LoadingState from "../../components/common/LoadingState";
import PendingActionNotice from "../../components/common/PendingActionNotice";
import GoalCreateDialog from "../../components/dashboard/GoalCreateDialog";
import GoalEditDialog from "../../components/dashboard/GoalEditDialog";
import CommitmentCreateDialog from "../../components/dashboard/CommitmentCreateDialog";
import CommitmentEditDialog from "../../components/dashboard/CommitmentEditDialog";
import VisionCreateDialog from "../../components/dashboard/VisionCreateDialog";
import VisionEditDialog from "../../components/dashboard/VisionEditDialog";
import PlanHeader from "./components/PlanHeader";
import FocusCard from "./components/FocusCard";
import WeeklyActionsCard from "./components/WeeklyActionsCard";
import WeekPreviewCard from "./components/WeekPreviewCard";
import ProgressStoryCard from "./components/ProgressStoryCard";
import usePlanData from "./hooks/usePlanData";
import {
  applyPlanReviewSuggestion,
  ensurePlanReview,
  fetchPlanReview,
} from "../../api/aiIntelligence";
import { hasNumericGoalTarget } from "./utils/planSelectors";

export default function PlanPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    loading,
    error,
    vision,
    selectedGoalId,
    setSelectedGoalId,
    goalSwitchOptions,
    selectedGoal,
    selectedGoalDueDate,
    selectedGoalDaysRemaining,
    visionText,
    actionRows,
    weekPreview,
    progressStory,
    loadPlan,
  } = usePlanData();

  const [creatingVision, setCreatingVision] = useState(false);
  const [editingVision, setEditingVision] = useState(false);
  const [creatingGoal, setCreatingGoal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [creatingCommitment, setCreatingCommitment] = useState(false);
  const [editingCommitment, setEditingCommitment] = useState(null);
  const [planEditorContext, setPlanEditorContext] = useState(null);
  const [weekShapeOpen, setWeekShapeOpen] = useState(false);
  const [goalDetailsOpen, setGoalDetailsOpen] = useState(false);
  const openedActionIdRef = React.useRef("");

  const handleOpenNewGoal = () => {
    if (!vision) {
      setCreatingVision(true);
      return;
    }
    setCreatingGoal(true);
  };

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const actionId = params.get("actionId") || params.get("commitmentId") || params.get("habitId") || "";
    if (!actionId || actionId === openedActionIdRef.current || !actionRows.length) return;
    const match =
      actionRows.find((row) => String(row.id) === String(actionId)) ||
      findCommitmentInVision(vision, actionId);
    if (match?.goalId && selectedGoalId !== match.goalId) {
      setSelectedGoalId(match.goalId);
      return;
    }
    if (!match?.commitment) return;
    openedActionIdRef.current = actionId;
    setEditingCommitment(match.commitment);
  }, [actionRows, location.search, selectedGoalId, setSelectedGoalId, vision]);

  const intelPlanContext = React.useMemo(
    () => buildIntelPlanContext(new URLSearchParams(location.search), editingCommitment),
    [editingCommitment, location.search]
  );

  const openPlanCheckHabitEditor = React.useCallback((suggestion) => {
    const habitId = suggestion?.habit_id;
    if (!habitId) return false;
    const match =
      actionRows.find((row) => String(row.id) === String(habitId)) ||
      findCommitmentInVision(vision, habitId);
    if (!match?.commitment) return false;
    if (match.goalId && selectedGoalId !== match.goalId) {
      setSelectedGoalId(match.goalId);
    }
    setPlanEditorContext(buildPlanEditorContextForSuggestion(suggestion, match.commitment));
    setEditingCommitment(match.commitment);
    return true;
  }, [actionRows, selectedGoalId, setSelectedGoalId, vision]);

  if (loading && !vision) {
    return (
      <Box sx={{ width: "100%", maxWidth: "760px", mx: "auto", px: { xs: 2, md: 5 }, pt: 3 }}>
        <LoadingState
          title="Preparing your plan"
          body="Bringing your vision, goals, and weekly habits into focus."
          rows={4}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1160px",
        mx: "auto",
        px: { xs: 2, md: 5 },
        pt: 3,
        pb: 6,
      }}
    >
      <PlanHeader onNewGoal={handleOpenNewGoal} />

      {error && (
        <Typography sx={{ mb: 2.5, color: "error.main" }}>
          {error}
        </Typography>
      )}

      {!selectedGoal ? (
        <DirectionSection
          visionText={visionText}
          goalTitle={selectedGoal?.title || ""}
          activeGoalCount={(vision?.goals || []).filter((goal) => goal?.is_active !== false).length}
          activeHabitCount={(vision?.goals || []).reduce((count, goal) => {
            const commitments = Array.isArray(goal.commitments) ? goal.commitments : [];
            return count + commitments.filter((item) => item?.is_active !== false).length;
          }, 0)}
          onEditVision={() => {
            if (vision) setEditingVision(true);
            else setCreatingVision(true);
          }}
          onAddGoal={handleOpenNewGoal}
        />
      ) : null}

      <FocusCard
        goal={selectedGoal}
        goalOptions={goalSwitchOptions}
        selectedGoalId={selectedGoalId}
        onSelectGoal={setSelectedGoalId}
        dueDate={selectedGoalDueDate}
        daysRemaining={selectedGoalDaysRemaining}
        hasNumericTarget={hasNumericGoalTarget(selectedGoal)}
        onNewGoal={handleOpenNewGoal}
        onEditGoal={() => {
          if (selectedGoal) setEditingGoal(selectedGoal);
        }}
        onUpdateProgress={hasNumericGoalTarget(selectedGoal) ? () => {
          if (selectedGoal) setEditingGoal(selectedGoal);
        } : undefined}
        onOpenDetails={selectedGoal ? () => setGoalDetailsOpen((value) => !value) : undefined}
      />

      {selectedGoal && goalDetailsOpen ? (
        <Stack spacing={2} sx={{ mt: 2.4 }}>
          <DirectionSection
            visionText={visionText}
            goalTitle={selectedGoal?.title || ""}
            activeGoalCount={(vision?.goals || []).filter((goal) => goal?.is_active !== false).length}
            activeHabitCount={(vision?.goals || []).reduce((count, goal) => {
              const commitments = Array.isArray(goal.commitments) ? goal.commitments : [];
              return count + commitments.filter((item) => item?.is_active !== false).length;
            }, 0)}
            onEditVision={() => {
              if (vision) setEditingVision(true);
              else setCreatingVision(true);
            }}
            onAddGoal={handleOpenNewGoal}
          />
        {progressStory.length ? (
          <ProgressStoryCard rows={progressStory} />
        ) : null}
        </Stack>
      ) : null}

      <Box sx={{ mt: 2.4 }}>
        <WeeklyActionsCard
          actions={actionRows}
          hasGoal={Boolean(selectedGoal)}
          goalTitle={selectedGoal?.title || null}
          onAddAction={() => setCreatingCommitment(true)}
          onEditAction={(commitment) => setEditingCommitment(commitment)}
        />
      </Box>

      <Box sx={{ mt: 2.4 }}>
        <PlanStrategistSection
          navigate={navigate}
          goalId={selectedGoal?.id || null}
          goalTitle={selectedGoal?.title || null}
          onPlanChanged={loadPlan}
          onOpenHabitEditor={openPlanCheckHabitEditor}
          intelContext={intelPlanContext}
        />
      </Box>

      <Box sx={{ mt: 2.4 }}>
        <WeekShapeDisclosureRow
          days={weekPreview}
          open={weekShapeOpen}
          onToggle={() => setWeekShapeOpen((value) => !value)}
        />
        {weekShapeOpen ? (
          <Box sx={{ mt: 1.5 }}>
            <WeekPreviewCard
              days={weekPreview}
              goalTitle={selectedGoal?.title || null}
              onHabitPress={(habitId) => {
                const match = actionRows.find((row) => String(row.id) === String(habitId));
                if (match?.commitment) setEditingCommitment(match.commitment);
              }}
              onStartHabit={(habit, date) => {
                navigate(`/today?habitId=${habit.id}&date=${date}`);
              }}
              onTrySmallVersion={(habit, date) => {
                navigate(`/today?habitId=${habit.id}&date=${date}&mode=small_version`);
              }}
              onAddHabit={() => setCreatingCommitment(true)}
            />
          </Box>
        ) : null}
      </Box>

      {!vision && !loading && (
        <Typography sx={{ mt: 2, color: "text.secondary" }}>
          Start with a vision so your goals and habits have a clear place to live.
        </Typography>
      )}

      {creatingVision && (
        <VisionCreateDialog
          onClose={() => setCreatingVision(false)}
          onSaved={() => {
            setCreatingVision(false);
            loadPlan();
          }}
        />
      )}

      {editingVision && vision && (
        <VisionEditDialog
          vision={vision}
          onClose={() => setEditingVision(false)}
          onSaved={() => {
            setEditingVision(false);
            loadPlan();
          }}
        />
      )}

      {creatingGoal && vision && (
        <GoalCreateDialog
          visionId={vision.id}
          onClose={() => setCreatingGoal(false)}
          onSaved={(createdGoal) => {
            setCreatingGoal(false);
            loadPlan();
            if (createdGoal?.id) {
              setSelectedGoalId(createdGoal.id);
            }
          }}
        />
      )}

      {editingGoal && (
        <GoalEditDialog
          goal={editingGoal}
          onClose={() => setEditingGoal(null)}
          onSaved={() => {
            setEditingGoal(null);
            loadPlan();
          }}
        />
      )}

      {creatingCommitment && selectedGoal && (
        <CommitmentCreateDialog
          goalId={selectedGoal.id}
          onClose={() => setCreatingCommitment(false)}
          onSaved={() => {
            setCreatingCommitment(false);
            loadPlan();
          }}
        />
      )}

      {editingCommitment && (
        <CommitmentEditDialog
          commitment={editingCommitment}
          intelContext={planEditorContext || intelPlanContext}
          onClose={() => {
            setPlanEditorContext(null);
            setEditingCommitment(null);
          }}
          onSaved={() => {
            setPlanEditorContext(null);
            setEditingCommitment(null);
            loadPlan();
          }}
        />
      )}
    </Box>
  );
}

function findCommitmentInVision(vision, actionId) {
  const goals = vision?.goals || [];
  for (const goal of goals) {
    const commitment = (goal.commitments || []).find((item) => String(item.id) === String(actionId));
    if (commitment) {
      return { id: commitment.id, commitment, goalId: goal.id };
    }
  }
  return null;
}

function DirectionSection({ visionText, goalTitle, activeGoalCount, activeHabitCount, onEditVision, onAddGoal }) {
  const hasVision = Boolean(visionText && visionText !== "Add the bigger picture behind this plan.");
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
      <Stack spacing={1.4}>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
              Direction
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 850, mt: 0.3 }}>
              {hasVision ? "The bigger picture" : "Set your direction"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={onEditVision}>
              {hasVision ? "Edit vision" : "Add vision"}
            </Button>
            <Button size="small" variant="outlined" onClick={onAddGoal}>
              Add goal
            </Button>
          </Stack>
        </Stack>
        <Typography color="text.secondary" sx={{ maxWidth: 780 }}>
          {hasVision ? visionText : "Give TrustBud the bigger picture behind your goals."}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between">
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              Current focus
            </Typography>
            <Typography sx={{ fontWeight: 850 }}>
              {goalTitle || "Choose your first goal"}
            </Typography>
          </Box>
          <Typography color="text.secondary" sx={{ fontWeight: 800 }}>
            {activeGoalCount} goals · {activeHabitCount} habits
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

function WeekShapeDisclosureRow({ days = [], open, onToggle }) {
  const today = days.find((day) => day.isToday) || days[0];
  const totalDue = days.reduce((sum, day) => sum + Number(day.dueCount || 0), 0);
  const totalDone = days.reduce((sum, day) => sum + Number(day.doneCount || 0), 0);
  const heavyDays = days.filter((day) => Number(day.dueCount || 0) >= 4).length;
  const todayOpen = today ? Math.max(Number(today.dueCount || 0) - Number(today.doneCount || 0), 0) : 0;
  const title = today ? `${today.dueCount || 0} today · ${todayOpen} open` : `${totalDue} planned`;
  const body = totalDue <= 0
    ? "No habits scheduled this week."
    : heavyDays
      ? `${heavyDays} heavier day${heavyDays === 1 ? "" : "s"} this week.`
      : `${totalDone}/${totalDue} planned habit slot${totalDue === 1 ? "" : "s"} done this week.`;

  return (
    <Paper
      component="button"
      type="button"
      variant="outlined"
      onClick={onToggle}
      sx={{
        width: "100%",
        borderRadius: 2,
        p: 1.75,
        bgcolor: "#fff",
        cursor: "pointer",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: alpha("#17345d", 0.08),
          color: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "0 0 auto",
        }}
      >
        <CalendarMonthOutlinedIcon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900, letterSpacing: 0.4 }}>
          This week
        </Typography>
        <Typography sx={{ fontWeight: 850, color: "text.primary", lineHeight: 1.25 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {body}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "primary.main", flex: "0 0 auto" }}>
        <Typography sx={{ fontSize: 13, fontWeight: 850 }}>
          {open ? "Hide" : "Open"}
        </Typography>
        {open ? <KeyboardArrowUpOutlinedIcon fontSize="small" /> : <KeyboardArrowDownOutlinedIcon fontSize="small" />}
      </Stack>
    </Paper>
  );
}

function buildIntelPlanContext(params, commitment) {
  if (params.get("source") !== "intel") return null;
  const sheet = params.get("sheet") || "";
  const review = params.get("review") || "";
  const focus = params.get("focus") || "";
  const suggestion = params.get("suggestion") || "";
  const habitTitle = commitment?.title || "this habit";
  if (review === "plan_load" && focus === "daily_set") {
    return {
      title: "From Insights: make today lighter.",
      body: `Insights noticed you have a lot of active habits, but today may only need the essentials. Decide whether ${habitTitle} should wait today.`,
      primaryField: "plan_check",
      saveLabel: "Save habit",
    };
  }
  if (review === "plan_load") {
    return {
      title: "From Insights: review your active plan.",
      body: "Insights noticed your plan may be carrying too much at once. Start with the whole-plan check.",
      primaryField: "plan_check",
      saveLabel: "Save habit",
    };
  }
  if (sheet === "planned_time") {
    return {
      title: "TrustBud is suggesting a planned time.",
      body: `${habitTitle} may need a clearer place in the day. Choose the time you want TrustBud to watch.`,
      primaryField: "planned_time",
      saveLabel: "Save planned time",
    };
  }
  if (sheet === "cue") {
    return {
      title: "TrustBud is suggesting a clearer cue.",
      body: `${habitTitle} needs a concrete start point. Add the routine, place, or situation where this should begin.`,
      primaryField: "cue_context",
      saveLabel: "Save cue",
    };
  }
  if (sheet === "schedule") {
    return {
      title: "TrustBud is suggesting a lighter schedule.",
      body: `${habitTitle} may not need the same pressure every day. Review the cadence and active days before saving.`,
      primaryField: "cadence",
      saveLabel: "Save schedule",
    };
  }
  if (sheet === "done_criteria") {
    return {
      title: "TrustBud is suggesting a clearer finish line.",
      body: `${habitTitle} needs a clearer definition of done so Today and Insights can track it correctly.`,
      primaryField: "done_criteria",
      saveLabel: "Save finish line",
    };
  }
  if (suggestion === "make_weekly" || suggestion === "change_cadence") {
    return {
      title: "TrustBud is suggesting weekly cadence.",
      body: `${habitTitle} may fit better as a weekly habit. Review the cadence before saving.`,
      primaryField: "cadence",
      suggestedFrequency: "weekly",
      saveLabel: "Save weekly cadence",
    };
  }
  if (sheet === "small_version") {
    return {
      title: "TrustBud is suggesting a clearer start.",
      body: `Review ${habitTitle} and add a cue, planned time, or check-in support. No habit setting changed yet.`,
      primaryField: "habit",
      saveLabel: "Save habit",
    };
  }
  if (sheet === "focus") {
    return {
      title: "TrustBud is suggesting this as a focus habit.",
      body: `Check that ${habitTitle} has the right cue and planned time before you make it today's focus.`,
      primaryField: "habit",
      saveLabel: "Save habit",
    };
  }
  return {
    title: "TrustBud sent you here to adjust this habit.",
    body: `Review ${habitTitle} and make the change that will make it easier to follow.`,
    primaryField: "habit",
    saveLabel: "Save habit",
  };
}

function planSuggestionEditorRoute(suggestion) {
  if (!suggestion?.habit_id) return "/plan?review=plan";
  const habitId = encodeURIComponent(String(suggestion.habit_id));
  if (suggestion.type === "meal_checkin_support") {
    return `/today?commitmentId=${habitId}&mode=plan_next_choice&source=plan_review`;
  }
  if (suggestion.type === "use_health_proof") {
    return `/today?commitmentId=${habitId}&mode=health_proof&source=plan_review`;
  }
  let sheet = "";
  let extra = "";
  if (suggestion.type === "add_planned_time" || suggestion.type === "find_calendar_time") {
    sheet = "planned_time";
  } else if (suggestion.type === "add_cue" || suggestion.type === "add_environment_cue") {
    sheet = "cue";
  } else if (suggestion.type === "clarify_habit") {
    sheet = "done_criteria";
  } else if (["move_to_weekdays", "move_to_weekends", "add_rest_days"].includes(suggestion.type)) {
    sheet = "schedule";
  } else if (suggestion.type === "change_cadence") {
    sheet = "schedule";
    extra = "&suggestion=change_cadence";
  }
  const sheetParams = sheet ? `&source=intel&sheet=${sheet}${extra}` : "";
  return `/plan?actionId=${habitId}&review=plan${sheetParams}`;
}

function buildPlanEditorContextForSuggestion(suggestion, commitment) {
  const habitTitle = commitment?.title || "this habit";
  if (suggestion?.type === "add_planned_time" || suggestion?.type === "find_calendar_time") {
    return {
      title: "Plan Check: add a smart nudge.",
      body: `${habitTitle} needs a clearer place in the day. Choose the time you want TrustBud to watch.`,
      primaryField: "planned_time",
      saveLabel: "Save planned time",
    };
  }
  if (suggestion?.type === "add_cue" || suggestion?.type === "add_environment_cue") {
    return {
      title: "Plan Check: add a clearer cue.",
      body: `${habitTitle} needs a concrete start point. Add the routine, place, or situation where this should begin.`,
      primaryField: "cue_context",
      saveLabel: "Save cue",
    };
  }
  if (suggestion?.type === "clarify_habit") {
    return {
      title: "Plan Check: clarify the finish line.",
      body: `${habitTitle} needs a clearer definition of done so Today and Insights can track it correctly.`,
      primaryField: "done_criteria",
      saveLabel: "Save finish line",
    };
  }
  if (["change_cadence", "move_to_weekdays", "move_to_weekends", "add_rest_days"].includes(suggestion?.type)) {
    return {
      title: "Plan Check: review the schedule.",
      body: `${habitTitle} may not need the same pressure every day. Review cadence and active days before saving.`,
      primaryField: "cadence",
      suggestedFrequency: suggestion?.type === "change_cadence" ? "weekly" : undefined,
      saveLabel: "Save schedule",
    };
  }
  return {
    title: "Plan Check: review this habit.",
    body: `Review ${habitTitle} and make the change that will make it easier to follow.`,
    primaryField: "habit",
    saveLabel: "Save habit",
  };
}

function cleanPlanText(value) {
  return String(value || "").trim();
}

function planRiskConfidenceText(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "strong") return "Strong evidence";
  if (normalized === "emerging") return "Emerging evidence";
  return "Still learning";
}

function planRiskEvidenceItems(evidence) {
  if (!Array.isArray(evidence)) return [];
  return evidence
    .map((item) => {
      if (typeof item === "string") return { label: "Evidence", value: cleanPlanText(item) };
      if (!item || typeof item !== "object") return null;
      const value = cleanPlanText(item.value || item.body || item.summary || "");
      if (!value) return null;
      return {
        label: cleanPlanText(item.label || item.name || "Evidence"),
        value,
      };
    })
    .filter(Boolean)
    .slice(0, 3);
}

function PlanConsistencyRiskCard({ risk, actionFallback = "Review Plan", onAction }) {
  if (!risk || typeof risk !== "object") return null;
  const title = cleanPlanText(risk.title || "Biggest risk");
  const claim = cleanPlanText(risk.claim || "");
  if (!title && !claim) return null;
  const evidence = planRiskEvidenceItems(risk.evidence);
  const why = cleanPlanText(risk.why_it_matters || "");
  const actionLabel = cleanPlanText(risk.recommended_next_action?.label || actionFallback);
  const isExample = Boolean(risk.example || risk.type === "example");
  return (
    <Box sx={{ border: "1px solid", borderColor: "#cfe2f3", bgcolor: "#f8fbff", borderRadius: 1.5, p: 1.5 }}>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900, letterSpacing: 0.4 }}>
          Biggest risk
        </Typography>
        <Chip size="small" label={isExample ? "Example only" : planRiskConfidenceText(risk.confidence)} sx={{ fontWeight: 850 }} />
      </Stack>
      <Typography sx={{ mt: 0.75, fontWeight: 850, color: "text.primary" }}>{title}</Typography>
      {claim ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.55 }}>
          {claim}
        </Typography>
      ) : null}
      {evidence.length ? (
        <Stack spacing={0.75} sx={{ mt: 1.15 }}>
          {evidence.map((item, index) => (
            <Stack key={`${item.label}-${index}`} direction="row" spacing={1} justifyContent="space-between">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>
                {item.label}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 850, textAlign: "right" }}>
                {item.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : null}
      {why ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.55 }}>
          <Box component="span" sx={{ fontWeight: 850, color: "text.primary" }}>Why it matters: </Box>
          {why}
        </Typography>
      ) : null}
      {actionLabel ? (
        <Button size="small" variant="contained" onClick={onAction} sx={{ mt: 1.25 }}>
          {actionLabel}
        </Button>
      ) : null}
    </Box>
  );
}

function PlanStrategistSection({ navigate, goalId, goalTitle, onPlanChanged, onOpenHabitEditor, intelContext }) {
  const [payload, setPayload] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [reviewing, setReviewing] = React.useState(false);
  const [applyingId, setApplyingId] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [error, setError] = React.useState("");
  const [reviewScope, setReviewScope] = React.useState("whole_plan");
  const [expandedChangeId, setExpandedChangeId] = React.useState("");
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const scopedGoalId = reviewScope === "goal" ? goalId : null;

  const loadReview = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchPlanReview({ scope: reviewScope, goalId: scopedGoalId });
      setPayload(data);
    } catch (err) {
      setError(err.message || "Plan review is unavailable right now.");
    } finally {
      setLoading(false);
    }
  }, [reviewScope, scopedGoalId]);

  React.useEffect(() => {
    loadReview();
  }, [loadReview]);

  React.useEffect(() => {
    if (!goalId && reviewScope === "goal") {
      setReviewScope("whole_plan");
    }
  }, [goalId, reviewScope]);

  React.useEffect(() => {
    if (intelContext?.primaryField === "plan_check" && reviewScope !== "whole_plan") {
      setReviewScope("whole_plan");
    }
  }, [intelContext?.primaryField, reviewScope]);

  const handleReview = async ({ forceRefresh = false } = {}) => {
    setReviewing(true);
    setNotice("");
    setError("");
    try {
      const data = await ensurePlanReview({ allowLiveAi: true, forceRefresh, scope: reviewScope, goalId: scopedGoalId });
      setPayload(data);
      setNotice(data.source === "ai_cached" ? "Plan review ready." : "Plan review ready. TrustBud checked your plan.");
    } catch (err) {
      setError(err.message || "Plan review could not run.");
    } finally {
      setReviewing(false);
    }
  };

  const handleApply = async (suggestion) => {
    if (!payload?.id || !suggestion?.id) return;
    if (suggestion.requires_user_edit) {
      if (suggestion.type === "meal_checkin_support" && suggestion.habit_id) {
        navigate(planSuggestionEditorRoute(suggestion));
        return;
      }
      if (suggestion.type === "use_health_proof" && suggestion.habit_id) {
        setNotice("Apple Health proof is available from the iPhone app. Open Today on your phone for this habit.");
        return;
      }
      if (suggestion.type === "make_today_lighter") {
        navigate("/today?source=plan&review=plan_load&focus=daily_set");
        return;
      }
      if (suggestion.habit_id) {
        if (onOpenHabitEditor?.(suggestion)) return;
        setError("Could not find that habit in the current plan.");
        return;
      }
      navigate("/plan?review=plan");
      return;
    }
    setApplyingId(suggestion.id);
    setNotice("");
    setError("");
    try {
      let result = await applyPlanReviewSuggestion({
        reviewId: payload.id,
        suggestionId: suggestion.id,
        action: "apply",
      });
      if (result?.requires_confirmation) {
        const confirmed = window.confirm(result.message || "This changes your plan. Apply it now?");
        if (!confirmed) {
          setNotice("No change made.");
          return;
        }
        result = await applyPlanReviewSuggestion({
          reviewId: payload.id,
          suggestionId: suggestion.id,
          action: "apply",
          userEdits: { confirm_plan_mutation: true },
        });
      }
      setNotice(result?.message || "Plan updated.");
      if (result?.next_route && result.next_route !== "/plan") {
        navigate(result.next_route);
      }
      if (result?.applied) {
        onPlanChanged?.();
        await loadReview();
      }
    } catch (err) {
      setError(err.message || "This change could not be applied.");
    } finally {
      setApplyingId("");
    }
  };

  const handleSkip = async (suggestion) => {
    if (!payload?.id || !suggestion?.id) return;
    setApplyingId(suggestion.id);
    setNotice("");
    setError("");
    try {
      await applyPlanReviewSuggestion({
        reviewId: payload.id,
        suggestionId: suggestion.id,
        action: "skip",
      });
      setPayload((current) => removeSuggestionFromPayload(current, suggestion.id));
      setNotice("Removed this suggestion from the list.");
      await loadReview();
    } catch (err) {
      setError(err.message || "Could not skip this suggestion.");
    } finally {
      setApplyingId("");
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={18} />
          <Box>
            <Typography sx={{ fontWeight: 800 }}>Checking for a saved Plan Check</Typography>
            <Typography variant="body2" color="text.secondary">
              Looking for the latest read on this plan.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  }

  if (payload?.locked) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
          TrustBud Plus
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 850, mt: 0.4 }}>
          Unlock Plan Insights
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.6 }}>
          See whether your habits actually support your goals, where the plan is too heavy, and what to simplify.
        </Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate("/trustbud-plus")}>
          Unlock TrustBud Plus
        </Button>
      </Paper>
    );
  }

  const review = payload?.review || {};
  const suggestions = Array.isArray(review.suggestions) ? review.suggestions.slice(0, 3) : [];
  const topChanges = buildTopChangeRows(review.top_changes, suggestions);
  const healthDimensions = Array.isArray(review.health_dimensions) ? review.health_dimensions : [];
  const attentionDimensions = healthDimensions.filter((item) => item?.status === "needs_attention");
  const goalHealth = Array.isArray(review.goal_health?.items) ? review.goal_health.items : [];
  const cadenceBuckets = Array.isArray(review.cadence_strategy)
    ? review.cadence_strategy
    : Array.isArray(review.cadence_strategy?.buckets)
      ? review.cadence_strategy.buckets
      : [];
  const lastAdjustment = review.last_adjustment_result || null;
  const hasReview = Boolean(
    review.biggest_consistency_risk?.claim ||
      review.biggest_consistency_risk?.title ||
      review.headline?.title ||
      topChanges.length ||
      healthDimensions.length
  );
  const retryAttemptsRemaining = Number(payload?.retry_attempts_remaining ?? 0);
  const canRetryReview = Boolean(payload?.can_retry_ai_review && retryAttemptsRemaining > 0);

  const primaryDiagnosis = review.plan_diagnosis?.body
      ? review.plan_diagnosis
      : review.headline?.title
        ? { title: review.headline.title, body: review.headline.body || "" }
        : null;
  const biggestRisk = review.biggest_consistency_risk || null;
  const biggestRiskTitle = cleanPlanText(biggestRisk?.title || "");
  const biggestRiskClaim = cleanPlanText(biggestRisk?.claim || "");
  const riskAction = biggestRisk?.recommended_next_action || {};
  const handleRiskAction = () => {
    if (riskAction?.deeplink) {
      navigate(riskAction.deeplink);
      return;
    }
    if (topChanges[0]?.id) {
      setExpandedChangeId(topChanges[0].id);
      return;
    }
    handleReview({ forceRefresh: true });
  };
  const primaryChange = topChanges[0] || null;
  const primarySuggestion = primaryChange?.suggestion || suggestions[0] || null;
  const collapsedBody = primaryChange?.expectedBenefit ||
    (primarySuggestion
      ? suggestionExpectedBenefitText(primarySuggestion, primaryChange?.reason || biggestRiskClaim || review.headline?.body)
      : biggestRiskClaim || review.headline?.body || reviewScopeBody(reviewScope, goalTitle));

  if (hasReview && !detailsOpen) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
        <Stack spacing={1.75}>
          {intelContext?.title && (
            <Alert severity="info">
              <Typography sx={{ fontWeight: 800 }}>{intelContext.title}</Typography>
              <Typography variant="body2">{intelContext.body}</Typography>
            </Alert>
          )}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
              Plan check
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 850, mt: 0.4 }}>
              {primarySuggestion ? "One plan improvement" : "Hold this plan steady"}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.6, maxWidth: 760 }}>
              {collapsedBody}
            </Typography>
          </Box>
          {reviewing && (
            <PendingActionNotice
              title="Reviewing your plan"
              body="TrustBud is checking goals, habits, planned times, and the clearest next step."
              steps={["Goals and habits", "Friction points", "Next step"]}
            />
          )}
          {notice && <Alert severity="success">{notice}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {primarySuggestion ? (
              <Button
                variant="contained"
                size="small"
                disabled={Boolean(applyingId)}
                onClick={() => handleApply(primarySuggestion)}
                startIcon={applyingId === primarySuggestion.id ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : null}
              >
                {applyingId === primarySuggestion.id ? "Saving change" : suggestionActionLabel(primarySuggestion)}
              </Button>
            ) : null}
            <Button variant="outlined" size="small" onClick={() => setDetailsOpen(true)}>
              Full plan read
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
            {reviewScope === "goal" && goalTitle ? `Goal check: ${goalTitle}` : "Whole plan check"}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 850, mt: 0.4 }}>
            {hasReview ? biggestRiskTitle || review.headline?.title || "Plan Health" : "Plan Health"}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.6 }}>
            {hasReview
              ? biggestRiskClaim || review.headline?.body || "TrustBud reviewed your goals and habits."
              : reviewScopeBody(reviewScope, goalTitle)}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.6 }}>
            <Button
              size="small"
              variant={reviewScope === "whole_plan" ? "contained" : "outlined"}
              onClick={() => setReviewScope("whole_plan")}
            >
              Whole plan
            </Button>
            <Button
              size="small"
              variant={reviewScope === "goal" ? "contained" : "outlined"}
              disabled={!goalId}
              onClick={() => setReviewScope("goal")}
            >
              This goal
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {reviewScopeBody(reviewScope, goalTitle)}
          </Typography>
        </Box>

        {intelContext?.title && (
          <Alert severity="info">
            <Typography sx={{ fontWeight: 800 }}>{intelContext.title}</Typography>
            <Typography variant="body2">{intelContext.body}</Typography>
          </Alert>
        )}

        <PlanConsistencyRiskCard
          risk={biggestRisk}
          actionFallback={topChanges[0]?.cta || "Review Plan"}
          onAction={handleRiskAction}
        />

        {primaryDiagnosis?.body && (
          <Box sx={{ bgcolor: "grey.50", borderRadius: 1.5, p: 1.5 }}>
            <Typography sx={{ fontWeight: 750 }}>{primaryDiagnosis.title || "Plan diagnosis"}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              {primaryDiagnosis.body}
            </Typography>
          </Box>
        )}

        {reviewing && (
          <PendingActionNotice
            title="Reviewing your plan"
            body="TrustBud is checking goals, habits, planned times, and safe changes. You can stay here while it works."
            steps={["Reading goals and habits", "Checking what can safely change", "Preparing plan suggestions"]}
          />
        )}

        {notice && <Alert severity="success">{notice}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}

        {healthDimensions.length > 0 && (
          <Box>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Plan health</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {healthDimensions.slice(0, 4).map((dimension) => (
                <Chip
                  key={dimension.key || dimension.label}
                  size="small"
                  color={dimensionChipColor(dimension.status)}
                  label={`${dimension.label || dimensionTitle(dimension.key)} · ${dimensionStatusText(dimension.status)}`}
                />
              ))}
            </Stack>
            {detailsOpen && attentionDimensions.length > 0 && (
              <Stack spacing={1} sx={{ mt: 1.25 }}>
                {attentionDimensions.slice(0, 3).map((dimension) => (
                  <Box key={dimension.key || dimension.label} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {dimension.label || dimensionTitle(dimension.key)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {dimension.summary}
                    </Typography>
                    {Array.isArray(dimension.evidence) && dimension.evidence.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.7 }}>
                        {dimension.evidence.slice(0, 2).join(" · ")}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {detailsOpen && goalHealth.length > 0 && (
          <Box>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>
              {review.goal_health?.title || (reviewScope === "goal" ? "This goal" : "Goals")}
            </Typography>
            <Stack spacing={1}>
              {goalHealth.slice(0, 3).map((goal, index) => (
                <Box key={goal.goal_id || `${goal.title}-${index}`} sx={{ bgcolor: "grey.50", borderRadius: 1.5, p: 1.25 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{goal.title}</Typography>
                    <Chip size="small" label={goalHealthStatusText(goal.status)} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
                    {goal.body}
                  </Typography>
                  {(goal.next_change || goal.next_structural_change) ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.7 }}>
                      Next: {goal.next_change || goal.next_structural_change}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {detailsOpen && lastAdjustment?.title && (
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.25 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" sx={{ fontWeight: 800 }}>Last adjustment</Typography>
              <Chip size="small" label={lastAdjustmentStatusText(lastAdjustment.status)} />
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 750, mt: 0.6 }}>
              {lastAdjustment.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {lastAdjustment.body}
            </Typography>
          </Box>
        )}

        {topChanges.length > 0 && (
          <Box>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>One change to test</Typography>
            <Stack spacing={1.25}>
              {topChanges.slice(0, 1).map((change, index) => {
                const suggestion = change.suggestion;
                const expanded = expandedChangeId === change.id;
                return (
                  <Box key={change.id || index} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.25 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800 }}>{change.title}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.55, fontWeight: 800 }}>
                          {suggestionActionConsequence(suggestion)}
                        </Typography>
                      </Box>
                      <Chip size="small" label={suggestionLabel(change.type)} />
                    </Stack>
                    <Stack spacing={0.75} sx={{ mt: 1.15 }}>
                      <PlanRecommendationRow label="Current issue" value={change.currentIssue} />
                      <PlanRecommendationRow label="Proposed change" value={change.proposedChange} />
                      <PlanRecommendationRow label="What changes" value={change.whatChanges} />
                      <PlanRecommendationRow label="What does not change" value={change.whatStaysSame} />
                      <PlanRecommendationRow label="Expected benefit" value={change.expectedBenefit} />
                      <PlanRecommendationRow label="Undo path" value={change.undoPath} />
                    </Stack>
                    {expanded && (
                      <Stack spacing={1} sx={{ mt: 1.1 }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                          <PlanDiffBox label="Before" value={change.before} />
                          <PlanDiffBox label="After" value={change.after} />
                        </Box>
                        {change.details && (
                          <Typography variant="body2" color="text.secondary">
                            {change.details}
                          </Typography>
                        )}
                        {change.surfaces?.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Used in: {change.surfaces.map(surfaceLabel).join(" · ")}
                          </Typography>
                        )}
                      </Stack>
                    )}
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                      {suggestion ? (
                        <Button
                          variant="contained"
                          size="small"
                          disabled={Boolean(applyingId)}
                          onClick={() => handleApply(suggestion)}
                          startIcon={applyingId === suggestion.id ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : null}
                        >
                          {applyingId === suggestion.id ? "Saving change" : change.cta || suggestionActionLabel(suggestion)}
                        </Button>
                      ) : null}
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setExpandedChangeId(expanded ? "" : change.id)}
                      >
                        {expanded ? "Hide before/after" : "Before/after"}
                      </Button>
                      {suggestion ? (
                        <Button
                          variant="text"
                          size="small"
                          disabled={Boolean(applyingId)}
                          onClick={() => handleSkip(suggestion)}
                        >
                          Not now
                        </Button>
                      ) : null}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {detailsOpen && cadenceBuckets.length > 0 && (
          <Box>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>Cadence strategy</Typography>
            <Stack spacing={1}>
              {cadenceBuckets.map((bucket) => (
                <Box key={bucket.key || bucket.label} sx={{ bgcolor: "grey.50", borderRadius: 1.5, p: 1.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {bucket.label || cadenceBucketLabel(bucket.key)}
                  </Typography>
                  {bucket.body && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {bucket.body}
                    </Typography>
                  )}
                  {Array.isArray(bucket.items) && bucket.items.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.9 }}>
                      {bucket.items.slice(0, 4).map((item, index) => (
                        <Chip key={item.habit_id || `${item.title}-${index}`} size="small" label={item.title} />
                      ))}
                    </Stack>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {detailsOpen && review.what_not_to_change?.length > 0 && (
          <Box>
            <Typography sx={{ fontWeight: 800 }}>What not to change</Typography>
            {review.what_not_to_change.slice(0, 2).map((item, index) => (
              <Typography key={`${item.title}-${index}`} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {item.title} {item.body}
              </Typography>
            ))}
          </Box>
        )}

        {review.learning_state?.not_enough_data && (
          <Alert severity="info">
            {review.learning_state.body || "TrustBud is still learning this plan. Check in for a few more days and the review will get sharper."}
          </Alert>
        )}

        {hasReview && (
          <Button variant="outlined" size="small" onClick={() => setDetailsOpen((value) => !value)}>
            {detailsOpen ? "Hide full plan read" : "Full plan read"}
          </Button>
        )}

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button variant={hasReview ? "outlined" : "contained"} onClick={() => handleReview()} disabled={reviewing}>
            {reviewing ? "Reviewing..." : "Review my plan"}
          </Button>
          {hasReview && canRetryReview && (
            <Button variant="text" onClick={() => handleReview({ forceRefresh: true })} disabled={reviewing}>
              Try again
            </Button>
          )}
          {payload?.cached && <Chip size="small" label="Saved review" />}
          {payload?.source === "deterministic" && <Chip size="small" label="TrustBud checked your plan" />}
        </Stack>
      </Stack>
    </Paper>
  );
}

function PlanDiffBox({ label, value }) {
  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", bgcolor: "grey.50", borderRadius: 1.5, p: 1.1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 750, mt: 0.25 }}>
        {value || "No change"}
      </Typography>
    </Box>
  );
}

function PlanRecommendationRow({ label, value }) {
  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", bgcolor: "grey.50", borderRadius: 1.5, p: 1.1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 850, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
        {value || "No change"}
      </Typography>
    </Box>
  );
}

function reviewScopeBody(scope, goalTitle) {
  if (scope === "goal" && goalTitle) {
    return `TrustBud will focus on the habits behind ${goalTitle}, while still checking how much the full plan is carrying.`;
  }
  return "TrustBud will look across all your goals and habits.";
}

function buildTopChangeRows(topChanges = [], suggestions = []) {
  const suggestionMap = new Map((suggestions || []).map((item) => [String(item.id), item]));
  const explicitRows = Array.isArray(topChanges)
    ? topChanges
        .slice(0, 1)
        .map((change, index) => {
          const suggestion =
            suggestionMap.get(String(change.suggestion_id || "")) ||
            suggestionMap.get(String(change.id || "")) ||
            suggestions.find((item) => item.type && item.type === change.type) ||
            null;
          return buildTopChangeRow(change, suggestion, index);
        })
    : [];
  if (explicitRows.length) return explicitRows;
  return (suggestions || []).slice(0, 1).map((suggestion, index) => buildTopChangeRow({}, suggestion, index));
}

function buildTopChangeRow(change = {}, suggestion = null, index = 0) {
  const source = suggestion || change || {};
  const id = String(change.id || change.suggestion_id || source.id || `change-${index}`);
  const title = change.title || suggestionTitle(source);
  const reason = change.reason || change.one_line_reason || change.body || suggestionExplanationText(source);
  return {
    id,
    suggestion,
    type: source.type || change.type || "plan_change",
    title,
    reason,
    details: change.details || source.why_it_matters || source.body || "",
    cta: change.cta || change.primary_cta || suggestionActionLabel(source),
    before: change.before || suggestionBeforeValue(source),
    after: change.after || suggestionAfterValue(source),
    currentIssue: suggestionCurrentIssueText(source, reason),
    proposedChange: title,
    whatChanges: suggestionChangeText(source),
    whatStaysSame: suggestionStableText(source),
    expectedBenefit: suggestionExpectedBenefitText(source, reason),
    undoPath: suggestionUndoPathText(source),
    surfaces: Array.isArray(change.affected_surfaces) && change.affected_surfaces.length
      ? change.affected_surfaces
      : suggestionAffectedSurfaces(source),
  };
}

function suggestionBeforeValue(suggestion) {
  const current = suggestion?.proposed_change?.current_value;
  if (current !== null && current !== undefined && String(current).trim()) return String(current);
  switch (suggestion?.type) {
    case "add_cue":
    case "add_environment_cue":
      return "No clear start";
    case "add_planned_time":
    case "find_calendar_time":
      return "No planned time";
    case "meal_checkin_support":
      return "Standard habit";
    case "change_cadence":
      return "Daily";
    case "pause_habit":
      return "Active in Plan";
    case "clarify_habit":
      return "Unclear habit wording";
    default:
      return "Current setup";
  }
}

function suggestionAfterValue(suggestion) {
  const proposed = suggestion?.proposed_change?.proposed_value;
  if (proposed !== null && proposed !== undefined && String(proposed).trim()) return String(proposed);
  switch (suggestion?.type) {
    case "add_cue":
    case "add_environment_cue":
      return "Clear start added";
    case "add_planned_time":
    case "find_calendar_time":
      return "Smart nudge set";
    case "meal_checkin_support":
      return "Check-in opens in Today";
    case "change_cadence":
      return "Weekly";
    case "pause_habit":
      return "Paused for now";
    case "clarify_habit":
      return "Clearer habit wording";
    default:
      return "Updated setup";
  }
}

function suggestionAffectedSurfaces(suggestion) {
  if (suggestion?.type === "meal_checkin_support") return ["today", "intel", "coach_checkins"];
  if (suggestion?.type === "add_planned_time" || suggestion?.type === "find_calendar_time") return ["today", "schedule", "coach_checkins"];
  if (["change_cadence", "move_to_weekdays", "move_to_weekends", "add_rest_days"].includes(suggestion?.type)) return ["plan", "today", "schedule"];
  if (suggestion?.type === "pause_habit") return ["plan", "today"];
  if (suggestion?.type === "make_today_lighter") return ["today", "intel"];
  if (suggestion?.type === "use_health_proof") return ["today", "proof"];
  return ["plan", "today", "intel"];
}

function surfaceLabel(surface) {
  return {
    today: "Today",
    intel: "Insights",
    coach_checkins: "Coach check-ins",
    weekly_summary: "Weekly Summary",
    schedule: "Schedule",
    plan: "Plan",
    proof: "Proof",
  }[surface] || String(surface || "").replace(/_/g, " ");
}

function dimensionTitle(key) {
  return {
    goal_alignment: "Goal alignment",
    daily_load: "Daily load",
    cadence_balance: "Cadence balance",
    timing: "Timing",
    recovery_support: "Recovery support",
    quiet_habits: "Quiet habits",
    clarity: "Clarity",
    support_fit: "Accountability fit",
  }[key] || "Plan health";
}

function dimensionStatusText(status) {
  if (status === "good") return "good";
  if (status === "needs_attention") return "needs attention";
  return "learning";
}

function dimensionChipColor(status) {
  if (status === "good") return "success";
  if (status === "needs_attention") return "warning";
  return "default";
}

function goalHealthStatusText(status) {
  return {
    supported: "supported",
    under_supported: "needs support",
    quiet: "going quiet",
    unclear: "unclear",
  }[status] || "learning";
}

function lastAdjustmentStatusText(status) {
  return {
    watching: "watching",
    helped: "helped",
    not_helping_yet: "not helping yet",
    too_early: "too early",
    no_data: "learning",
  }[status] || "watching";
}

function cadenceBucketLabel(key) {
  return {
    daily_core: "Daily core",
    weekly_support: "Weekly support",
    monthly_review: "Monthly review",
    can_wait: "Can wait",
    paused: "Paused",
  }[key] || "Cadence";
}

function suggestionLabel(type) {
  return {
    add_planned_time: "Planned time",
    find_calendar_time: "Calendar time",
    add_cue: "Start",
    add_environment_cue: "Start",
    change_cadence: "Cadence",
    move_to_weekdays: "Schedule",
    move_to_weekends: "Schedule",
    add_rest_days: "Schedule",
    pause_habit: "Pause",
    clarify_habit: "Clarify",
    add_habit_to_goal: "Goal support",
    move_habit_to_goal: "Goal alignment",
    make_today_lighter: "Lighten today",
    meal_checkin_support: "Check-in",
    use_health_proof: "Proof option",
  }[type] || "Plan change";
}

function suggestionActionLabel(suggestion) {
  if (suggestion.type === "meal_checkin_support") return "Start check-in";
  if (suggestion.type === "pause_habit") return "Pause this habit";
  if (suggestion.type === "clarify_habit") return "Clarify habit";
  if (suggestion.type === "add_habit_to_goal") return "Add habit";
  if (suggestion.type === "make_today_lighter") return "Review today";
  if (suggestion.type === "find_calendar_time" || suggestion.type === "add_planned_time") return "Add smart nudge";
  if (suggestion.type === "use_health_proof") return "Use on iPhone";
  if (suggestion.type === "move_to_weekdays") return "Use weekdays";
  if (suggestion.type === "move_to_weekends") return "Use weekends";
  if (suggestion.type === "add_rest_days") return "Add rest days";
  if (suggestion.type === "add_cue" || suggestion.type === "add_environment_cue") return "Add cue";
  if (suggestion.type === "change_cadence") return "Make weekly";
  if (suggestion.requires_user_edit) return suggestion.primary_cta || "Review change";
  return suggestion.primary_cta || "Review change";
}

function suggestionActionConsequence(suggestion) {
  if (!suggestion) return "Review before applying";
  if (suggestion.type === "meal_checkin_support") return "Opens Today";
  if (suggestion.type === "use_health_proof") return "Needs iPhone";
  if (suggestion.type === "find_calendar_time") return "Opens habit timing";
  if (suggestion.requires_user_edit) return "Opens editor";
  if (suggestion.safe_to_apply) {
    return ["change_cadence", "pause_habit", "move_to_weekdays", "move_to_weekends", "add_rest_days"].includes(suggestion.type)
      ? "Needs confirmation"
      : "Saves now";
  }
  return "Review before saving";
}

function suggestionTitle(suggestion) {
  return {
    meal_checkin_support: "Start a check-in",
    add_planned_time: "Add a smart nudge",
    find_calendar_time: "Find a real opening",
    add_cue: "Add a starting point",
    add_environment_cue: "Put the start in the right place",
    change_cadence: "Make this weekly",
    move_to_weekdays: "Show this on weekdays",
    move_to_weekends: "Try this on weekends",
    add_rest_days: "Add rest days",
    pause_habit: "Pause this for now",
    clarify_habit: "Make this habit clearer",
    add_habit_to_goal: "Add a habit to this goal",
    make_today_lighter: "Make today lighter",
    use_health_proof: "Use Health proof on iPhone",
  }[suggestion.type] || String(suggestion.title || "Adjust this plan").replace(/\.$/, "");
}

function suggestionExplanationText(suggestion) {
  const explanation = {
    meal_checkin_support: "This works better as a quick check-in before eating, not as a timed habit.",
    find_calendar_time: "Use Calendar to choose a planned time that fits around the day you already have.",
    add_planned_time: "This habit needs a clearer place in the day.",
    add_cue: "This habit needs a clear moment or place to begin.",
    add_environment_cue: "This habit needs the right routine, place, or decision moment to begin.",
    change_cadence: "This habit has gone quiet as a daily habit.",
    move_to_weekdays: "This habit may work better when it stays out of the weekend plan.",
    move_to_weekends: "This habit may need less weekday competition.",
    add_rest_days: "This habit is showing every day, but the plan may need intentional off days.",
    pause_habit: "This habit is active in your plan, but it has not been part of your week.",
    clarify_habit: "TrustBud needs to know what counts as done.",
    add_habit_to_goal: "This goal matters, but it does not have an active habit behind it yet.",
    make_today_lighter: "Too many habits are competing for the same day.",
    use_health_proof: "Private proof is available when matching activity is already recorded.",
  }[suggestion.type];
  return explanation || dedupeSentences([suggestion.body, suggestion.why_it_matters].filter(Boolean).join(" "));
}

function suggestionCurrentIssueText(suggestion, fallback = "") {
  const issue = {
    meal_checkin_support: "This habit is easier to keep as a check-in before the decision, not as another timed task.",
    find_calendar_time: "This habit does not have a realistic opening on the calendar yet.",
    add_planned_time: "This habit does not have a clear time for TrustBud to watch.",
    add_cue: "This habit does not have a clear starting point.",
    add_environment_cue: "This habit depends on context, but the starting place or decision moment is not set.",
    change_cadence: "This habit may be too heavy as a daily commitment.",
    move_to_weekdays: "This habit may be competing with weekend routines.",
    move_to_weekends: "This habit may be competing with weekday routines.",
    add_rest_days: "This habit is asking for daily pressure when intentional off days may fit better.",
    pause_habit: "This habit is active, but it has gone quiet.",
    clarify_habit: "This habit needs a clearer finish line.",
    add_habit_to_goal: "This goal does not have an active habit behind it.",
    make_today_lighter: "Today is carrying too many open actions.",
    use_health_proof: "This habit may already have private proof available, but it is not connected to the report flow.",
  }[suggestion?.type];
  return fallback || suggestion?.body || issue || "This part of the plan can be made easier to keep.";
}

function suggestionChangeText(suggestion) {
  const type = suggestion?.type;
  if (type === "meal_checkin_support") return "Today will open a private check-in for this habit. This will not mark the habit done automatically.";
  if (type === "find_calendar_time") return "You will review Calendar openings, choose a realistic planned time, and decide whether to add it to Calendar.";
  if (type === "add_planned_time") return "You will choose when this habit should show up. TrustBud can then check in if that time passes.";
  if (type === "add_cue") return "You will add the routine, place, or situation where this habit should start.";
  if (type === "add_environment_cue") return "You will put the habit near the decision moment, routine boundary, or place where it actually starts.";
  if (type === "change_cadence") return "This habit will show up weekly instead of every day.";
  if (type === "move_to_weekdays") return "This habit will show up Monday through Friday. Weekends stay off.";
  if (type === "move_to_weekends") return "This habit will show up Saturday and Sunday. Weekdays stay off.";
  if (type === "add_rest_days") return "This habit will keep a lighter weekday schedule with intentional off days.";
  if (type === "pause_habit") return "It will stop showing up in Today. You can bring it back later.";
  if (type === "add_habit_to_goal") return "You will create one small habit connected to this goal.";
  if (type === "clarify_habit") return "You will edit the habit wording so Today can track it more clearly.";
  if (type === "make_today_lighter") return "TrustBud will help you choose what is Start here, what is Keep light, and what can wait.";
  if (type === "use_health_proof") return "The iPhone app can attach private proof when matching Health activity is already available.";
  return "TrustBud will open the right editor so you can review the change first.";
}

function suggestionStableText(suggestion) {
  if (suggestion?.type === "meal_checkin_support") {
    return "The habit target, schedule, and completion rules stay the same. Nothing is marked done automatically.";
  }
  if (suggestion?.type === "use_health_proof") {
    return "The habit still requires your confirmation. Health data stays private unless you attach proof from iPhone.";
  }
  if (suggestion?.type === "make_today_lighter") {
    return "Your goals and habits stay in Plan until you choose a specific edit.";
  }
  if (suggestion?.type === "pause_habit") {
    return "The habit history, goal, notes, and proof stay saved.";
  }
  if (["change_cadence", "move_to_weekdays", "move_to_weekends", "add_rest_days"].includes(suggestion?.type)) {
    return "The habit name, goal, proof rules, and history stay the same.";
  }
  if (suggestion?.requires_user_edit) return "Nothing changes until you review and save it.";
  return "The rest of the plan stays unchanged.";
}

function suggestionExpectedBenefitText(suggestion, fallback = "") {
  const benefit = {
    meal_checkin_support: "You get a lower-friction moment to stay honest before the hard decision happens.",
    find_calendar_time: "A real opening makes the habit feel scheduled instead of hopeful.",
    add_planned_time: "TrustBud can nudge at the right moment and stop treating the habit like it can happen anytime.",
    add_cue: "A clear cue makes the first step easier to start.",
    add_environment_cue: "Putting the cue near the real decision point reduces reliance on willpower.",
    change_cadence: "A weekly rhythm can keep the habit alive without crowding Today.",
    move_to_weekdays: "Weekends stop carrying a habit that fits the workweek better.",
    move_to_weekends: "Weekdays get lighter while the habit keeps a defined place.",
    add_rest_days: "Today gets less crowded without deleting the habit.",
    pause_habit: "The active plan gets lighter and less noisy.",
    clarify_habit: "Today and Insights can tell what actually counts.",
    add_habit_to_goal: "The goal gets one concrete behavior behind it.",
    make_today_lighter: "You can pick what matters today instead of dragging every habit around.",
    use_health_proof: "Manual proof work goes down when matching activity already exists.",
  }[suggestion?.type];
  return suggestion?.why_it_matters || fallback || benefit || "This should make the plan easier to follow for the next few days.";
}

function suggestionUndoPathText(suggestion) {
  if (suggestion?.type === "pause_habit") return "Reactivate the habit from the habit editor.";
  if (["change_cadence", "move_to_weekdays", "move_to_weekends", "add_rest_days"].includes(suggestion?.type)) {
    return "Open the habit editor and change the cadence or active days back.";
  }
  if (suggestion?.type === "meal_checkin_support" || suggestion?.type === "use_health_proof") {
    return "Back out of Today without starting or attaching anything.";
  }
  if (suggestion?.requires_user_edit) return "Close the editor without saving.";
  return "Open the related habit and change it back.";
}

function removeSuggestionFromPayload(payload, suggestionId) {
  if (!payload?.review?.suggestions) return payload;
  return {
    ...payload,
    review: {
      ...payload.review,
      suggestions: payload.review.suggestions.filter((item) => item.id !== suggestionId),
    },
  };
}

function dedupeSentences(text) {
  const seen = new Set();
  return String(text || "")
    .split(/(?<=[.!?])\s+/)
    .map((part) => part.trim())
    .filter((part) => {
      const key = part.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .join(" ");
}
